import React, {useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import {Button, CircularProgress, FormControlLabel, Switch} from "@mui/material";
import {ManageAccounts} from "@mui/icons-material";
import {GiCoinsPile} from "../icons/startupGameIcons";
import Swal from "sweetalert2";
import {byteConverter, getErrorMessage, isEmailValid, updateCachedUser} from "../utilities/Utils";
import DropboxChooserButton from './DropboxChooserButton';
import DropboxUrlInput from './DropboxUrlInput';
import CreatorTermsModal from './CreatorTermsModal';
import {DEFAULT_PROFILE_AVATAR, profileAvatarUrl} from '../utilities/profileAvatar';
import {useGenreTerms} from '../utilities/genreIcons';

const DARK = 'var(--lm-bg-1a1f2b)';
const BORDER = 'rgba(var(--th-6c4f9b-rgb), 0.4)';
const LABEL_COLOR = 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a55))';

function ProfileModal({showProfile, setShowProfile}) {
    const terms = useGenreTerms();
    const userJSON = JSON.parse(sessionStorage.getItem('user'));

    const [loading, setLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [filePreview, setFilePreview] = useState(null);
    const [fileSize, setFileSize] = useState(null);
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [dropboxUrl, setDropboxUrl] = useState(null);
    const [formData, setFormData] = useState({emailAddress: '', fileType: '', avatarBorderColor: ''});
    const [isCreator, setIsCreator] = useState(!!userJSON.isCreator);
    const [creatorToggling, setCreatorToggling] = useState(false);
    const [creatorTermsAccepted, setCreatorTermsAccepted] = useState(false);
    const [showCreatorTerms, setShowCreatorTerms] = useState(false);
    const [acceptingTerms, setAcceptingTerms] = useState(false);

    useEffect(() => {
        if (!showProfile) return;
        const headers = {'Authorization': 'Bearer ' + userJSON.accessToken};
        fetch(`${process.env.REACT_APP_API_PROXY}/profile/getProfileForUser/${userJSON.id}`, {headers})
            // No profile row yet is a normal state for a new account, not a failure; res.json()
            // on a non-OK response would reject and leave the preview empty.
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                if (data) setFormData(data);
                // Avatars are served from S3 (dragondropvtt-avatars-public) by fileName now —
                // the avatar_image bytea column this used to decode is gone. No upload → default.
                setFilePreview(profileAvatarUrl(data?.fileName));
            })
            .catch(() => setFilePreview(DEFAULT_PROFILE_AVATAR));
        fetch(`${process.env.REACT_APP_API_PROXY}/auth/creatorTermsStatus`, {headers})
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => {
                setCreatorTermsAccepted(!!data.accepted);
                setIsCreator(!!data.isCreator);
                updateCachedUser({isCreator: !!data.isCreator});
            })
            .catch(() => setCreatorTermsAccepted(false));
    }, [showProfile]);

    function handleChange(e) {
        setFormData(prev => ({...prev, [e.target.name]: e.target.value}));
    }

    function handleClose() {
        setValidated(false);
        setFile(null);
        setFilePreview(null);
        setFileSize(null);
        setFileName(null);
        setDropboxUrl(null);
        setShowProfile(false);
    }

    function applyDropboxUrl(url) {
        setDropboxUrl(url);
        setFilePreview(url);
        setFile(null);
        setFileName(null);
    }

    function applyFile(f) {
        if (!f.name.match(/\.(jpg|jpeg|png|gif|tiff)$/i)) {
            Swal.fire({timer: 2000, title: 'Error', text: 'Please upload a valid image file', icon: 'error'});
            return;
        }
        if (f.size > 1000000) {
            Swal.fire({timer: 2000, title: 'Error', text: 'File size exceeds 1MB. Please upload a smaller image.', icon: 'error'});
            return;
        }
        setFileName(f.name);
        setFileSize(byteConverter(f.size, 1));
        setFilePreview(URL.createObjectURL(f));
        setFile(f);
        setDropboxUrl(null);
    }

    function handleFileInput(e) {
        if (e.target?.files[0]) applyFile(e.target.files[0]);
    }

    async function handleSaveProfile() {
        setValidated(true);
        if (!isEmailValid(formData.emailAddress)) return;

        setLoading(true);
        const profileFormData = new FormData();
        if (file) profileFormData.append('file', file);
        profileFormData.append('profile', new Blob(
            [JSON.stringify(dropboxUrl ? {...formData, dropboxUrl} : formData)],
            {type: 'application/json'}
        ));

        try {
            const response = await fetch(`${process.env.REACT_APP_API_PROXY}/profile/saveProfile?type=profile`, {
                method: 'POST',
                headers: {'Authorization': 'Bearer ' + userJSON.accessToken},
                body: profileFormData,
            });
            if (!response.ok) throw new Error(await getErrorMessage(response));
            Swal.fire({timer: 2000, title: 'Saved', text: 'Profile saved successfully', icon: 'success', position: 'bottom-end'});
            handleClose();
        } catch (err) {
            Swal.fire({timer: 2500, title: 'Failed', text: err.message, icon: 'error', position: 'bottom-end'});
        } finally {
            setLoading(false);
        }
    }

    async function doToggleCreator() {
        setCreatorToggling(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/toggleCreator`, {
                method: 'POST',
                headers: {'Authorization': 'Bearer ' + userJSON.accessToken},
            });
            if (!response.ok) throw new Error(await getErrorMessage(response));
            const data = await response.json();
            setIsCreator(data.isCreator);
            updateCachedUser({isCreator: data.isCreator});
        } catch (err) {
            Swal.fire({timer: 2500, title: 'Failed', text: err.message, icon: 'error', position: 'bottom-end'});
        } finally {
            setCreatorToggling(false);
        }
    }

    function handleToggleCreator() {
        if (creatorToggling) return;
        if (!isCreator && !creatorTermsAccepted) {
            setShowCreatorTerms(true);
            return;
        }
        doToggleCreator();
    }

    async function handleAcceptCreatorTerms() {
        setAcceptingTerms(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/acceptCreatorTerms`, {
                method: 'POST',
                headers: {'Authorization': 'Bearer ' + userJSON.accessToken},
            });
            if (!response.ok) throw new Error(await getErrorMessage(response));
            setCreatorTermsAccepted(true);
            setShowCreatorTerms(false);
            await doToggleCreator();
        } catch (err) {
            Swal.fire({timer: 2500, title: 'Failed', text: err.message, icon: 'error', position: 'bottom-end'});
        } finally {
            setAcceptingTerms(false);
        }
    }

    const emailInvalid = validated && !isEmailValid(formData.emailAddress);

    return (
        <Modal show={showProfile} onHide={handleClose} centered scrollable data-bs-theme="dark">
            {/* Header — one row rather than stacked. The header is fixed while the body
                scrolls, so every pixel here is permanently unavailable to the content. */}
            <Modal.Header closeButton style={{
                backgroundColor: DARK,
                borderBottom: `1px solid ${BORDER}`,
                background: `radial-gradient(ellipse at 50% 0%, rgba(var(--th-6c4f9b-rgb), 0.35) 0%, ${DARK} 80%)`,
                alignItems: 'center',
                paddingTop: 12, paddingBottom: 12,
            }}>
                <ManageAccounts style={{fontSize: 26, color: 'var(--lm-tx-th-c9a0f0)', filter: 'drop-shadow(0 0 6px rgba(var(--th-c9a0f0-rgb), 0.5))', marginRight: 10}}/>
                <Modal.Title style={{color: 'var(--lm-tx-th-c9a0f0)', fontWeight: 700, letterSpacing: '0.02em', fontSize: '1.05rem'}}>
                    Profile Settings
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{backgroundColor: DARK, color: 'var(--lm-tx-f5f5f5)', padding: '20px 24px'}}>
                {/* Avatar preview */}
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: 20}}>
                    <div
                        onClick={() => filePreview && window.open(filePreview, '_blank')}
                        style={{
                            cursor: filePreview ? 'pointer' : 'default',
                            width: 84, height: 84, borderRadius: '50%',
                            border: `3px solid ${formData.avatarBorderColor || 'var(--th-6c4f9b)'}`,
                            overflow: 'hidden',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: 'var(--lm-bg-2c2f36)',
                        }}
                    >
                        {filePreview
                            ? <img src={filePreview} alt="avatar" style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
                            : <ManageAccounts style={{fontSize: 44, color: 'rgba(var(--lm-tx-f5f5f5-rgb),0.2)'}}/>
                        }
                    </div>
                </div>

                {/* File upload */}
                <div style={{marginBottom: 16}}>
                    <label style={{display: 'block', fontSize: '0.8rem', color: LABEL_COLOR, marginBottom: 6}}>
                        Avatar - max 1 MB (.jpg .jpeg .png .gif .tiff)
                    </label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileInput}
                        style={{
                            display: 'block', width: '100%',
                            fontSize: '0.8rem', color: 'var(--lm-tx-f5f5f5)',
                            backgroundColor: 'var(--lm-bg-2c2f36)',
                            border: `1px solid ${BORDER}`,
                            borderRadius: 4, padding: '5px 8px',
                        }}
                    />
                    {fileName && (
                        <span style={{fontSize: '0.75rem', color: LABEL_COLOR, marginTop: 4, display: 'block'}}>
                            {fileName} ({fileSize})
                        </span>
                    )}
                    <div style={{marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6}}>
                        <DropboxChooserButton extensions={['.jpg', '.jpeg', '.png', '.gif', '.tiff']} onFile={applyFile} style={{alignSelf: 'flex-start'}}/>
                        <DropboxUrlInput onUrl={applyDropboxUrl}/>
                    </div>
                </div>

                {/* Border color */}
                <div style={{marginBottom: 16}}>
                    <label style={{display: 'block', fontSize: '0.8rem', color: LABEL_COLOR, marginBottom: 6}}>
                        Avatar Border Color
                    </label>
                    <input
                        type="color"
                        name="avatarBorderColor"
                        value={formData.avatarBorderColor || '#6C4F9B'}
                        onChange={handleChange}
                        title="Avatar border color"
                        style={{width: 40, height: 32, border: `1px solid ${BORDER}`, borderRadius: 4, backgroundColor: 'transparent', cursor: 'pointer', padding: 2}}
                    />
                </div>

                {/* Email */}
                <div style={{marginBottom: 8}}>
                    <label style={{display: 'block', fontSize: '0.8rem', color: LABEL_COLOR, marginBottom: 6}}>
                        Email Address
                    </label>
                    <input
                        name="emailAddress"
                        type="text"
                        value={formData.emailAddress}
                        onChange={handleChange}
                        style={{
                            display: 'block', width: '100%',
                            fontSize: '0.875rem', color: 'var(--lm-tx-f5f5f5)',
                            backgroundColor: 'var(--lm-bg-2c2f36)',
                            border: `1px solid ${emailInvalid ? '#f44336' : BORDER}`,
                            borderRadius: 4, padding: '7px 10px',
                            outline: 'none',
                        }}
                    />
                    {emailInvalid && (
                        <span style={{fontSize: '0.75rem', color: '#f44336', marginTop: 4, display: 'block'}}>
                            Please enter a valid email address.
                        </span>
                    )}
                </div>

                {/* Hoard creator opt-in */}
                <div style={{
                    marginTop: 16, padding: '10px 12px', borderRadius: 6,
                    border: `1px solid ${BORDER}`, backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <GiCoinsPile style={{fontSize: 20, color: 'var(--lm-tx-th-c9a0f0)'}}/>
                        <div>
                            <div style={{fontSize: '0.85rem', color: 'var(--lm-tx-f5f5f5)'}}>Become a Creator</div>
                            <div style={{fontSize: '0.72rem', color: LABEL_COLOR}}>
                                Lets you publish maps, tokens, and other assets to the {terms.Hoard}.
                            </div>
                        </div>
                    </div>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isCreator}
                                onChange={handleToggleCreator}
                                disabled={creatorToggling}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {color: 'var(--lm-tx-th-c9a0f0)'},
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {backgroundColor: 'var(--th-6c4f9b)'},
                                }}
                            />
                        }
                        label=""
                        sx={{m: 0}}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer style={{backgroundColor: DARK, borderTop: `1px solid ${BORDER}`, padding: '12px 24px'}}>
                <Button onClick={handleClose} disabled={loading} sx={{color: 'var(--lm-tx-9e9e9e)', '&:hover': {color: 'var(--lm-tx-f5f5f5)'}}}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} sx={{color: 'var(--lm-tx-f5f5f5)'}}/> : <ManageAccounts/>}
                    sx={{
                        ml: 1, backgroundColor: 'var(--th-6c4f9b)',
                        '&:hover': {backgroundColor: 'var(--th-7d5cb3)'},
                        '&:disabled': {backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.3)', color: 'rgba(245,245,245,0.3)'},
                    }}
                >
                    {loading ? 'Saving…' : 'Save Profile'}
                </Button>
            </Modal.Footer>

            <CreatorTermsModal
                show={showCreatorTerms}
                busy={acceptingTerms}
                onAccept={handleAcceptCreatorTerms}
                onDecline={() => setShowCreatorTerms(false)}
            />
        </Modal>
    );
}

export default ProfileModal;